export enum SortingQueryType {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
}

/** 与后端 WarehouseBusinessType / sorting_dashboard_hourly_summary.business_type 一致 */
export const WarehouseBusinessType = {
    SORTING: 1,
    LAST_MILE: 2,
} as const;

export interface SortingDashboardQueryInput {
    queryType: SortingQueryType;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    hubId: number;
    /** 客户名过滤；null/不传 时不过滤 */
    customerName?: string | null;
    /** 仓库类型：1=分拣仓，2=尾程仓；不传时按 1 处理 */
    businessType?: number | null;
    /** 非空时批量查询；空字符串项可表示全美（hub_id=0） */
    customerNames?: string[] | null;
}

export interface SortingDashboardItem {
    timePeriod: string;
    hubId: number;
    sortingCode?: string;
    inboundCount: number;
    /** 错分率专用分母；为 0 时不展示错分率（与后端一致，不回退入库量） */
    inboundWrongSortDenominatorCount?: number;
    /** 应到未到数（与 shouldArriveNotArriveRate 口径一致） */
    shouldArriveNotArriveCount: number;
    shouldArriveNotArriveRate?: string;
    outbound12hRate?: string;
    ascanMissRate?: string;
    transferArrivalMissRate?: string;
    transferArrivalDscanCount: number;
    transferArrivalMissCount: number;
    dscanCount: number;
    ascanMissCount: number;
    outboundMissRate?: string;
    outboundMissCount: number;
    wrongSortRate?: string;
    wrongSortCount: number;
    wrongSortToWrongHubACount: number;
    wrongSortToWrongWhsBCount: number;
    wrongSortToWrongLineCCount: number;
    lostDamagedRate?: string;
    lostDamagedCount: number;
    sortEfficiency?: string;
    sortPeak: number;
    /** 整体漏扫率（尾程仓：(ascan_miss + transfer_arrival_miss) / (dscan + transfer_arrival_dscan)） */
    overallMissScanRate?: string;
    /** 次日交接率（next_day_handover_count / inbound_count） */
    nextDayHandoverRate?: string;
    /** 次日交接数 */
    nextDayHandoverCount: number;
    /** 跨境客户24小时上网率（cross_border_online_24h_count / cross_border_handover_count），仅尾程仓 tab 展示 */
    crossBorderOnline24hRate?: string;
    /** 跨境24h上网数（分子） */
    crossBorderOnline24hCount?: number;
    /** 跨境交接包裹数（分母） */
    crossBorderHandoverCount?: number;
    /** 最终未交接 - 未正确装包（sort_error_type=1） */
    notCorrectlyPackedCount?: number;
    /** 最终未交接 - 包牌未发运（sort_error_type=2） */
    bagLabelNotShippedCount?: number;
    /** 最终未交接 - 没有发车动作（sort_error_type=3） */
    noDepartureActionCount?: number;
}

/** 聚合项：分子加总/分母加总，用于日均/周均/月均列；环比由后端计算 */
export interface SortingDashboardAggregateItem {
    hubId: number;
    sortingCode?: string;
    inboundCount: number;
    /** 错分率分母（所选范围内加总），与错分展开子项百分比分母一致 */
    inboundWrongSortDenominatorCount?: number;
    /** 应到未到数（所选范围内加总） */
    shouldArriveNotArriveCount: number;
    shouldArriveNotArriveRate?: string;
    outbound12hRate?: string;
    ascanMissRate?: string;
    transferArrivalMissRate?: string;
    transferArrivalDscanCount: number;
    transferArrivalMissCount: number;
    dscanCount: number;
    ascanMissCount: number;
    outboundMissRate?: string;
    outboundMissCount: number;
    wrongSortRate?: string;
    wrongSortCount: number;
    wrongSortToWrongHubACount: number;
    wrongSortToWrongWhsBCount: number;
    wrongSortToWrongLineCCount: number;
    lostDamagedRate?: string;
    lostDamagedCount: number;
    sortEfficiency?: string;
    sortPeak: number;
    /** 整体漏扫率（尾程仓；汇总后计算） */
    overallMissScanRate?: string;
    /** 次日交接率（汇总后计算） */
    nextDayHandoverRate?: string;
    /** 次日交接数（汇总） */
    nextDayHandoverCount: number;
    /** 跨境客户24小时上网率（汇总后计算），仅尾程仓 tab 展示 */
    crossBorderOnline24hRate?: string;
    /** 跨境24h上网数（汇总，分子） */
    crossBorderOnline24hCount?: number;
    /** 跨境交接包裹数（汇总，分母） */
    crossBorderHandoverCount?: number;
    /** 最终未交接 - 未正确装包（sort_error_type=1，汇总） */
    notCorrectlyPackedCount?: number;
    /** 最终未交接 - 包牌未发运（sort_error_type=2，汇总） */
    bagLabelNotShippedCount?: number;
    /** 最终未交接 - 没有发车动作（sort_error_type=3，汇总） */
    noDepartureActionCount?: number;
    inboundCountChange?: string;
    shouldArriveNotArriveRateChange?: string;
    outbound12hRateChange?: string;
    ascanMissRateChange?: string;
    transferArrivalMissRateChange?: string;
    outboundMissRateChange?: string;
    wrongSortRateChange?: string;
    lostDamagedRateChange?: string;
    sortEfficiencyChange?: string;
    sortPeakChange?: string;
    /** 整体漏扫率环比 */
    overallMissScanRateChange?: string;
    /** 次日交接率环比 */
    nextDayHandoverRateChange?: string;
}

export interface SortingDashboardCustomerSlice {
    customerName?: string | null;
    items: SortingDashboardItem[];
    aggregate: SortingDashboardAggregateItem[];
}

export interface QuerySortingDashboardResponse {
    querySortingDashboard: {
        items: SortingDashboardItem[];
        aggregate: SortingDashboardAggregateItem[];
        customerSlices?: SortingDashboardCustomerSlice[] | null;
        /** 本次查询命中行的 MAX(updated_at) ISO-8601 UTC 字符串；空结果时为 null */
        latestCalculatedAt?: string | null;
    };
}
